import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChalkboard, FaUsers } from "react-icons/fa";
import ErrorPage from "../../../components/ErrorPage";
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";
import { assignTeacherToClassroom, listClassroomTeacherAssignments, unassignTeacherFromClassroom, updateClassroom } from "../api";
import { loadClassroomDetails, loadSchoolYearList, loadTeacherList, upsertCachedClassroom } from "../cache";
import { listSubjects } from "../api";
import { ActionButton, BackButton, Field, LdsPage, StatusBadge, cardEntryClass, inputClass } from "../components/Lds";
import type { AppError, Classroom, ClassroomAssignmentDraft, ClassroomFormValues, SchoolYear, Subject, Teacher, TeacherRole } from "../types";
import { entityKey, httpStatus, schoolYearLabel, teacherName } from "../utils";
import { translateClassName } from "../../../utils/classroomUtils";

import { useLeaveForm } from "../../../components/navigation/BackButton";
import { useFormDirtyState, useUnsavedChangesGuard } from "../../../components/navigation/UnsavedChangesGuard";
const roles: TeacherRole[] = ["CLASS_TEACHER", "MENTOR", "SUBJECT_TEACHER", "ASSISTANT"];

const readableRoleFallback = (role: TeacherRole) => {
  const label = String(role).toLowerCase().replaceAll("_", " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const ClassroomEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const { t } = useTranslation();
  const [classroom, setClassroom] = useState<Classroom | null>(null);
  const [values, setValues] = useState<ClassroomFormValues | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [teacherAssignmentsDraft, setTeacherAssignmentsDraft] = useState<ClassroomAssignmentDraft[]>([]);
  const [newTeacherId, setNewTeacherId] = useState("");
  const [newRole, setNewRole] = useState<TeacherRole>("MENTOR");
  const [newSubjectIds, setNewSubjectIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<"classroom" | "teachers">("classroom");
  const [gradeInputEnabled, setGradeInputEnabled] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  // The team member being picked is not part of it until "Add to team"; only added/removed members count.
  const { isDirty, markClean } = useFormDirtyState({ values, teacherAssignments: teacherAssignmentsDraft });
  const { leaveWithoutWarning } = useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!id) return;
      try {
        const [classroomData, teacherList, yearList, assigned, subjectList] = await Promise.all([
          loadClassroomDetails(id),
          loadTeacherList(),
          loadSchoolYearList(),
          listClassroomTeacherAssignments(id),
          listSubjects(),
        ]);
        if (!cancelled) {
          setClassroom(classroomData);
          setTeachers(teacherList);
          setSchoolYears(yearList);
          setSubjects(subjectList.filter((subject) => subject.active));
          const loadedValues = {
            className: classroomData.className ?? "",
            schoolYearId: classroomData.schoolYear?.id != null ? String(classroomData.schoolYear.id) : "",
            grade: classroomData.grade != null ? String(classroomData.grade) : "",
            maxChildren: classroomData.maxChildren != null ? String(classroomData.maxChildren) : "",
            classType: classroomData.classType ?? "",
          };
          const loadedAssignments: ClassroomAssignmentDraft[] = assigned.map((assignment) => ({
            id: assignment.id ?? null,
            teacherId: assignment.teacherId ?? "",
            role: assignment.role ?? "PRIMARY",
            subjectIds: assignment.subjectIds?.map(String) ?? [],
            action: "keep" as const,
          })).filter((assignment) => assignment.teacherId !== "");
          setValues(loadedValues);
          setTeacherAssignmentsDraft(loadedAssignments);
          markClean({ values: loadedValues, teacherAssignments: loadedAssignments });
        }
      } catch (err) {
        if (!cancelled) {
          setError({
            message:
              httpStatus(err) === 403
                ? t("error.403.message", { defaultValue: "You do not have permission." })
                : t("classrooms.edit.load_failed", { defaultValue: "Failed to load classroom details." }),
            status: httpStatus(err),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [id, markClean, t]);

  const visibleAssignments = useMemo(() => teacherAssignmentsDraft.filter((assignment) => assignment.action !== "remove"), [teacherAssignmentsDraft]);
  const eligibleTeachers = useMemo(() => teachers.filter((teacher) => {
    const capabilities = teacher.capabilities ?? [];
    return capabilities.length === 0 || capabilities.includes(newRole) || (newRole === "CLASS_TEACHER" && capabilities.includes("PRIMARY"));
  }), [newRole, teachers]);
  const availableSubjects = useMemo(() => {
    const selectedTeacher = teachers.find((teacher) => String(teacher.id) === newTeacherId);
    const specialistIds = selectedTeacher?.subjects?.map((subject) => String(subject.id)) ?? [];
    return subjects.filter((subject) => {
      const gradeMatches = !classroom?.grade || subject.grades.length === 0 || subject.grades.includes(classroom.grade);
      const specialismMatches = specialistIds.length === 0 || specialistIds.includes(String(subject.id));
      return gradeMatches && specialismMatches;
    });
  }, [classroom?.grade, newTeacherId, subjects, teachers]);

  const addAssignment = () => {
    if (!newTeacherId || (newRole === "SUBJECT_TEACHER" && newSubjectIds.length === 0)) return;
    setTeacherAssignmentsDraft((prev) => [...prev, { id: null, teacherId: newTeacherId, role: newRole, subjectIds: newRole === "SUBJECT_TEACHER" ? newSubjectIds : [], action: "add" }]);
    setNewTeacherId("");
    setNewSubjectIds([]);
  };

  const markTeacherForUnassignment = (teacherId: string | number, role: TeacherRole) => {
    setTeacherAssignmentsDraft((prev) =>
      prev
        .map((assignment) => {
          if (String(assignment.teacherId) === String(teacherId) && assignment.role === role) {
            if (assignment.action === "add") return null;
            if (assignment.action === "keep") return { ...assignment, action: "remove" as const };
          }
          return assignment;
        })
        .filter((assignment): assignment is ClassroomAssignmentDraft => assignment !== null)
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id || !values) return;
    setSaving(true);
    try {
      const updated = await updateClassroom(id, values);
      const additions = teacherAssignmentsDraft.filter((assignment) => assignment.action === "add");
      const removals = teacherAssignmentsDraft.filter((assignment) => assignment.action === "remove");
      for (const assignment of additions) {
        await assignTeacherToClassroom(assignment.teacherId, id, assignment.role, assignment.subjectIds);
      }
      for (const assignment of removals) {
        if (assignment.id != null) {
          await unassignTeacherFromClassroom(assignment.id);
        }
      }
      upsertCachedClassroom(updated);
      markClean({ values, teacherAssignments: teacherAssignmentsDraft });
      setShowSuccessModal(true);
    } catch (err) {
      setError({
        message:
          httpStatus(err) === 403
            ? t("error.403.message", { defaultValue: "You do not have permission." })
            : t("classrooms.edit.update_failed", { defaultValue: "Failed to update classroom." }),
        status: httpStatus(err),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;
  if (!classroom || !values) return null;

  return (
    <LdsPage maxWidth="max-w-7xl">
      <div className="space-y-5">
        <BackButton fallback={`/school/classrooms/${id}/details`} />

        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 items-center gap-5 pr-0 lg:pr-[30%]">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-[24px] border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-2xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
              <FaChalkboard aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-indigo-600 dark:text-indigo-300">
                {t("classrooms.edit.title", { defaultValue: "Edit classroom" })}
              </p>
              <h1 className="mt-1 truncate text-3xl font-bold tracking-tight text-slate-950 dark:text-white">{translateClassName(classroom)}</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                {t("classrooms.edit.subtitle", { defaultValue: "Update classroom details and teacher assignments." })}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusBadge tone="info">{schoolYearLabel(classroom.schoolYear) || t("classrooms.filters.unknown_year", { defaultValue: "Unknown year" })}</StatusBadge>
                <StatusBadge tone="neutral"><span className="inline-flex items-center gap-1.5"><FaUsers aria-hidden="true" />{t("classrooms.edit.assignment_badge", { defaultValue: "Teacher assignments" })}</span></StatusBadge>
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex" aria-hidden="true">
            <FaChalkboard className="text-[120px] text-indigo-200/70 dark:text-indigo-800/40" />
          </div>
        </section>

        <form onSubmit={handleSubmit} className={`${cardEntryClass} rounded-[26px] border border-slate-200/80 bg-white/95 p-5 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-6`}>
          <div className="mb-5 grid gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-1.5 dark:border-white/10 dark:bg-slate-950/40 sm:grid-cols-2" role="tablist">
            {(["classroom", "teachers"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                onClick={() => setActiveTab(tab)}
                className={`relative min-h-[54px] rounded-[18px] px-4 text-sm font-semibold transition ${
                  activeTab === tab
                    ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:bg-indigo-400/10 dark:text-indigo-100 dark:ring-indigo-300/20"
                    : "text-slate-600 hover:bg-white dark:text-slate-300 dark:hover:bg-white/5"
                }`}
              >
                {tab === "classroom" ? t("classrooms.edit.tabs.classroom", { defaultValue: "Classroom" }) : t("classrooms.edit.tabs.teachers", { defaultValue: "Teachers" })}
                {activeTab === tab ? <span className="absolute inset-x-5 bottom-1.5 h-1 rounded-full bg-indigo-600 dark:bg-indigo-300" /> : null}
              </button>
            ))}
          </div>

          {activeTab === "classroom" ? (
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t("classrooms.overview.table.name", { defaultValue: "Name" })}>
                <input className={inputClass} value={values.className} onChange={(event) => setValues({ ...values, className: event.target.value })} required />
              </Field>
              <Field label={t("classrooms.edit.max_children", { defaultValue: "Maximum children" })}>
                <input className={inputClass} type="number" min={1} max={99} value={values.maxChildren} onChange={(event) => setValues({ ...values, maxChildren: event.target.value })} required />
              </Field>
              <Field label={t("classrooms.edit.select_year", { defaultValue: "Select school year" })}>
                <select className={inputClass} value={values.schoolYearId} onChange={(event) => setValues({ ...values, schoolYearId: event.target.value })} required>
                  <option value="">{t("classrooms.edit.select_year_dropdown_fill", { defaultValue: "Select a school year" })}</option>
                  {schoolYears.map((year) => (
                    <option key={String(year.id)} value={String(year.id)}>{schoolYearLabel(year)}</option>
                  ))}
                </select>
              </Field>
              {values.classType === "GRADE" ? (
                <Field label={t("classrooms.edit.grade_number", { defaultValue: "Grade number" })}>
                  <input
                    className={inputClass}
                    type="number"
                    min={1}
                    max={12}
                    value={values.grade}
                    onFocus={(event) => {
                      if (!gradeInputEnabled) {
                        event.currentTarget.blur();
                        setShowGradeModal(true);
                      }
                    }}
                    onChange={(event) => setValues({ ...values, grade: event.target.value })}
                    required
                  />
                </Field>
              ) : null}
            </div>
          ) : (
            <div className="space-y-5">
              <section>
                <h2 className="text-sm font-semibold uppercase text-slate-600 dark:text-slate-300">{t("teachers.table.current_teachers", { defaultValue: "Current teachers" })}</h2>
                {visibleAssignments.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">{t("classrooms.table.no_teacher_assigned", { defaultValue: "No teacher assigned" })}</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {visibleAssignments.map((assignment) => {
                      const teacher = teachers.find((item) => String(item.id) === String(assignment.teacherId));
                      return (
                        <div key={`${assignment.id ?? "new"}-${assignment.role}-${entityKey(assignment.teacherId)}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-white/10 dark:bg-slate-950/40">
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white">{teacherName(teacher) || "—"}</p>
                            <StatusBadge tone="info">{t(`classrooms.roles.${String(assignment.role).toLowerCase()}`, { defaultValue: readableRoleFallback(assignment.role) })}</StatusBadge>
                            {assignment.subjectIds.length ? <p className="mt-1 text-xs text-slate-500">{subjects.filter((subject) => assignment.subjectIds.includes(String(subject.id))).map((subject) => subject.name).join(", ")}</p> : null}
                          </div>
                          <button type="button" className="text-sm font-semibold text-red-600 dark:text-red-300" onClick={() => markTeacherForUnassignment(assignment.teacherId, assignment.role)}>
                            {t("common.remove", { defaultValue: "Remove" })}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4 dark:border-indigo-300/10 dark:bg-indigo-400/5">
                <h2 className="mb-4 font-semibold text-slate-900 dark:text-white">{t("classrooms.edit.add_team_member", { defaultValue: "Add team member" })}</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t("teachers.details.teacher_role", { defaultValue: "Teacher" })}><select className={inputClass} value={newTeacherId} onChange={(event) => { setNewTeacherId(event.target.value); setNewSubjectIds([]); }}><option value="">{t("classrooms.edit.select_teacher", { defaultValue: "Select a teacher" })}</option>{eligibleTeachers.map((teacher) => <option key={String(teacher.id)} value={String(teacher.id)}>{teacherName(teacher)}</option>)}</select></Field>
                  <Field label={t("classrooms.edit.function", { defaultValue: "Function" })}><select className={inputClass} value={newRole} onChange={(event) => { setNewRole(event.target.value); setNewSubjectIds([]); }}>{roles.map((role) => <option key={role} value={role}>{t(`classrooms.roles.${role.toLowerCase()}`, { defaultValue: readableRoleFallback(role) })}</option>)}</select></Field>
                </div>
                {newRole === "SUBJECT_TEACHER" ? <div className="mt-4"><p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100">{t("subjects.title", { defaultValue: "Subjects" })}</p><div className="flex flex-wrap gap-2">{availableSubjects.map((subject) => <label key={String(subject.id)} className={`cursor-pointer rounded-full border px-3 py-2 text-sm ${newSubjectIds.includes(String(subject.id)) ? "border-indigo-400 bg-white text-indigo-800 dark:bg-indigo-400/10 dark:text-indigo-100" : "border-slate-200 dark:border-white/10"}`}><input className="sr-only" type="checkbox" checked={newSubjectIds.includes(String(subject.id))} onChange={() => setNewSubjectIds((ids) => ids.includes(String(subject.id)) ? ids.filter((id) => id !== String(subject.id)) : [...ids, String(subject.id)])} />{subject.name}</label>)}</div></div> : null}
                <div className="mt-4 flex justify-end"><ActionButton onClick={addAssignment} disabled={!newTeacherId || (newRole === "SUBJECT_TEACHER" && newSubjectIds.length === 0)}>{t("classrooms.edit.add_to_team", { defaultValue: "Add to team" })}</ActionButton></div>
              </section>
            </div>
          )}

          <div className="sticky bottom-4 z-20 mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/80 bg-white/95 p-3 shadow-lg backdrop-blur dark:border-white/10 dark:bg-slate-950/90">
            <ActionButton tone="secondary" onClick={() => leaveWithoutWarning(() => navigate(`/school/classrooms/${id}/details`))}>{t("common.cancel", { defaultValue: "Cancel" })}</ActionButton>
            <ActionButton type="submit" disabled={saving}>{saving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save", { defaultValue: "Save" })}</ActionButton>
          </div>
        </form>
      </div>

      <Modal
        show={showGradeModal}
        type="confirm"
        title={t("common.modal.are_you_sure", { defaultValue: "Are you sure?" })}
        message={t("classrooms.edit.confirm_grade_change", { defaultValue: "Changing the grade can affect classroom planning. Continue?" })}
        isConfirm
        onConfirm={() => {
          setGradeInputEnabled(true);
          setShowGradeModal(false);
        }}
        onClose={() => setShowGradeModal(false)}
      />
      <Modal
        show={showSuccessModal}
        type="success"
        title={t("common.modal.success", { defaultValue: "Success" })}
        message={t("classrooms.edit.update_success", { defaultValue: "Classroom updated successfully." })}
        onClose={() => leaveForm(`/school/classrooms/${id}/details`)}
      />
    </LdsPage>
  );
};

export default ClassroomEdit;
